import React, { useState, useEffect } from 'react';

// Component for rendering a list of events/activities with PDF links
const ActivitiesList = ({ items }) => {
    // FIX: Normalize 'items' to always be an array.
    // The API sometimes returns an Array [...] and sometimes an Object { "0": ... }
    let safeItems = [];

    if (Array.isArray(items)) {
        // It is already an array (Standard NSS data)
        safeItems = items;
    } else if (items && typeof items === 'object') {
        // It is an object (UBA data), convert values to array
        safeItems = Object.values(items);
    }

    const groupedActivities = safeItems.reduce((acc, item) => {
        const year = item.year_select || 'Other Activities';
        if (!acc[year]) {
            acc[year] = [];
        }
        acc[year].push(item);
        return acc;
    }, {});

    const years = Object.keys(groupedActivities);
    const initialActiveYear = years.length > 0 ? years[0] : '';
    
    const [activeYearTab, setActiveYearTab] = useState(initialActiveYear); 

    // Early return if no data
    if (safeItems.length === 0) {
        return null;
    }
    
    const handleYearTabChange = (year) => {
        setActiveYearTab(year);
    };

    return (
        <div>
            {/* Mobile Select Menu for Years */}
            {/* <div className="d-flex align-items-center mb-4 flex-wrap gap-3 d-md-none">
                <label htmlFor="year-select-mobile" className="form-label mb-0 fw-semibold me-2">Select Year:</label>
                <select
                    className="form-select w-auto table-select"
                    id="year-select-mobile"
                    onChange={(e) => handleYearTabChange(e.target.value)}
                    value={activeYearTab}
                >
                    {years.map(year => (
                        <option key={year} value={year}>{year}</option>
                    ))}
                </select>
            </div> */}

            {/* Desktop Tabs for Years */}
            <ul className="nav nav-tabs custom-nav-tabs mb-3 gap-3 d-md-flex" id="activityYearTabs" role="tablist">
                {years.map((year) => (
                    <li className="nav-item" role="presentation" key={`year-tab-${year}`}>
                        <button
                            className={`nav-link ${activeYearTab === year ? 'active' : ''}`}
                            onClick={() => handleYearTabChange(year)}
                        >
                            {year}
                        </button>
                    </li>
                ))}
            </ul>
            
            {/* Content Display */}
            <div id="year-content-display">
                {Object.entries(groupedActivities).map(([year, activities]) => (
                    <div 
                        key={year} 
                        className="nss-year-content" 
                        style={{ display: activeYearTab === year ? 'block' : 'none' }}
                    >
                        <ul className="fa-ul custom-list justifyr">
                            {activities.map((activity, index) => (
                                <li key={`activity-${year}-${index}`}><span class="fa-li"><i class="fa fa-dot-circle-o"></i></span>
                                    <a href={`${process.env.REACT_APP_API_URL || ''}/public${activity.pdf_path}`} target="_blank" rel="noopener noreferrer" className="event-link">
                                        {activity.text} <i className="fa fa-file-pdf-o pdf-icon"></i>
                                    </a>
                                </li>
                            ))}
                        </ul>
                    </div>
                ))}
            </div>
        </div>
    );
};

const NSSContent = ({ contentBlocks, mainTabs }) => {
    // --- HOOKS CALLED AT TOP LEVEL ---
   
    
    // State Initialization: Calculate initial values before using useState
    const initialMainTabTitle = mainTabs.length > 0 ? mainTabs[0].title : null;
    const initialNssTabTitle = mainTabs[0]?.inner_tabs.length > 0 ? mainTabs[0].inner_tabs[0].title : null;

    const [activeMainTab, setActiveMainTab] = useState(initialMainTabTitle);
    const [activeNssTab, setActiveNssTab] = useState(initialNssTabTitle);
    
    
    // --- Utility and Data Access (After Hooks) ---
    const renderContent = (text) => {
        if (!text) return { __html: '' };
        return {
            __html: text
                .replace(/\r\n/g, '<br/>')
                .replace(/\n/g, '<br/>')
                .replace(/\r/g, '<br/>')
        };
    };
    
    const mainBlock = contentBlocks.length > 0 ? contentBlocks[0] : {};
    const additionalBlocks = contentBlocks.length > 1 ? contentBlocks.slice(1) : [];

    const activeMainTabData = mainTabs.find(tab => tab.title === activeMainTab);
    const activeNssTabData = activeMainTabData?.inner_tabs.find(tab => tab.title === activeNssTab);

    // --- Handlers ---

    const handleMainTabChange = (tabTitle) => {
        setActiveMainTab(tabTitle);
        const newMainTab = mainTabs.find(tab => tab.title === tabTitle);
        // Reset inner tab to the first one of the newly activated main tab
        if (newMainTab && newMainTab.inner_tabs && newMainTab.inner_tabs.length > 0) {
            setActiveNssTab(newMainTab.inner_tabs[0].title);
        } else {
            setActiveNssTab(null);
        }
    };

    const handleNssTabChange = (tabTitle) => {
        setActiveNssTab(tabTitle);
    };

    // Scroll logic
    useEffect(() => {
        function setupMainTabScrolling() {
            const tabContainer = document.querySelector('.naac-tabs-vertical .nav');
            if (!tabContainer) return;
            
            const mainTabButtons = tabContainer.querySelectorAll('.nav-link');

            mainTabButtons.forEach(button => {
                button.addEventListener('click', function (e) {
                    const targetContentId = e.target.getAttribute('data-bs-target');
                    if (!targetContentId) return;

                    setTimeout(() => {
                        const targetContent = document.querySelector(targetContentId);
                        if (targetContent) {
                            targetContent.scrollIntoView({
                                behavior: 'smooth',
                                block: 'start'
                            });
                        }
                    }, 50); 
                });
            });
        }
        setupMainTabScrolling();
    }, [activeMainTab, activeNssTab]);

    if (mainTabs.length === 0 && contentBlocks.length === 0) return null;

    return (
        <>
            {/* --- Main Content Blocks --- */}
            {mainBlock.text && (
                <section>
                    <div className="container">
                        <div className="sec-title text-center">
                            <h2>{mainBlock.heading}</h2>
                        </div>

                        <div className="row gap-3 gap-md-0 d-flex justify-content-center align-items-center">
                            {/* Image Column */}
                            {mainBlock.image_url && (
                                <div 
                                    className={`col-md-6 col-12 order-1 order-md-${mainBlock.image_position === 'right' ? '2' : '1'}`} 
                                    
                                >
                                    <div className="card glow-card border-0">
                                        <div className="card-body p-0 overflow-hidden">
                                            <img src={`${process.env.REACT_APP_API_URL}/public${mainBlock.image_url}`} className="img-fluid rounded" alt={mainBlock.heading} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Text Column */}
                            <div 
                                className={`col-md-6 col-12 order-2 order-md-${mainBlock.image_position === 'right' ? '1' : '2'}`} 
                              
                            >
                                
                                <p className="justify" dangerouslySetInnerHTML={renderContent(mainBlock.text)} />
                            </div>
                        </div>
                    </div>
                </section>
            )}

            {/* --- Additional Activity Block --- */}
            {additionalBlocks.map((block, index) => (
                block.heading && (
                    <section key={`content-block-${index + 1}`}>
                        <div className="container">
                            <div className="sec-title text-center">
                                <h2>{block.heading}</h2>
                            </div>

                            <div className="row mb-3 d-flex justify-content-center align-items-center">
                                {block.image_url && (
                                    <div
                                        className={`col-md-6 col-12 order-1 order-md-${block.image_position === 'right' ? '2' : '1'}`}
                                    >
                                        <img
                                            src={`${process.env.REACT_APP_API_URL}/public${block.image_url}`}
                                            alt={block.heading}
                                            className="img-fluid"
                                        />
                                    </div>
                                )}
                                {block.text && (
                                    <div
                                        className={`col-md-6 col-12 mt-3 order-2 order-md-${block.image_position === 'right' ? '1' : '2'}`}
                                    >
                                        <p className="justify" dangerouslySetInnerHTML={renderContent(block.text)} />
                                    </div>
                                )}
                            </div>
                        </div>
                    </section>
                )
            ))}

            {/* --- Dynamic Main Tabs Section --- */}
            {mainTabs.length > 0 && (
                <section className="pb_80">
                    <div className="container-fluid">
                        {/* Desktop Tabs Navigation */}
                        <ul className="nav nav-tabs main-tab align-items-center d-flex justify-content-center d-none d-md-flex" id="mainTabs" role="tablist">
                            {mainTabs.map((mainTab, index) => (
                                <li className="nav-item" role="presentation" key={`maintab-btn-${index}`}>
                                    <button
                                        className={`nav-link ${activeMainTab === mainTab.title ? 'active' : ''}`}
                                        onClick={() => handleMainTabChange(mainTab.title)}
                                    >
                                        <div className="naac-header">
                                            <h2>{mainTab.title}</h2>
                                        </div>
                                    </button>
                                </li>
                            ))}
                        </ul>

                        {/* Mobile Accordion Navigation */}
                        <div className="accordion mobile-accordion d-md-none" id="mobileAccordion">
                            {mainTabs.map((mainTab, mainIndex) => (
                                <div className="accordion-item" key={`mobile-main-item-${mainIndex}`}>
                                    <h2 className="accordion-header" id={`mobileHeader${mainIndex}`}>
                                        <button
                                            className={`accordion-button ${activeMainTab === mainTab.title ? '' : 'collapsed'}`}
                                            type="button"
                                            onClick={() => handleMainTabChange(mainTab.title)}
                                        >
                                            {mainTab.title}
                                        </button>
                                    </h2>
                                    <div
                                        id={`mobileCollapse${mainIndex}`}
                                        className={`accordion-collapse collapse ${activeMainTab === mainTab.title ? 'show' : ''}`}
                                        aria-labelledby={`mobileHeader${mainIndex}`}
                                    >
                                        <div className="accordion-body">
                                            <div className="container">
                                                <div className="accordion" id={`verticalMobileAccordion${mainIndex}`}>
                                                    {mainTab.inner_tabs && mainTab.inner_tabs.map((innerTab, innerIndex) => (
                                                        <div className="accordion-item" key={`mobile-inner-item-${mainIndex}-${innerIndex}`}>
                                                            <h2 className="accordion-header" id={`mobile-inner-header-${mainIndex}-${innerIndex}`}>
                                                                <button
                                                                    className={`accordion-button ${activeNssTab === innerTab.title ? '' : 'collapsed'}`}
                                                                    type="button"
                                                                    onClick={() => handleNssTabChange(innerTab.title)}
                                                                >
                                                                    {innerTab.title}
                                                                </button>
                                                            </h2>
                                                            <div
                                                                id={`mobile-inner-collapse-${mainIndex}-${innerIndex}`}
                                                                className={`accordion-collapse collapse ${activeNssTab === innerTab.title ? 'show' : ''}`}
                                                                aria-labelledby={`mobile-inner-header-${mainIndex}-${innerIndex}`}
                                                            >
                                                                <div className="accordion-body">
                                                                    <h5 className="mb-3 d-flex justify-content-center align-items-center">{innerTab.title}</h5>
                                                                    {innerTab.content_type === 'table' && innerTab.content && (
                                                                        <div dangerouslySetInnerHTML={renderContent(innerTab.content)} />
                                                                    )}
                                                                    {innerTab.content_type === 'list' && (
                                                                        <ActivitiesList items={innerTab.items} />
                                                                    )}
                                                                    {/* Handle optional image display for UBA, for example */}
                                                                    {mainTab.title.includes('UBA') && innerTab.content_type === 'list' && (
                                                                        <img src="https://care.edu.in/wp-content/uploads/2021/03/uba.jpg" alt="UBA Image" className="img-fluid mt-3" />
                                                                    )}
                                                                    {innerTab.content_type === 'data_table' && innerTab.table && (
                                                                        <div className="table-responsive">
                                                                        <table className="table table-bordered">
                                                                            <thead>
                                                                                <tr>
                                                                                    {innerTab.table.headers.map((h, i) => <th key={i}>{h}</th>)}
                                                                                </tr>
                                                                            </thead>
                                                                            <tbody>
                                                                                {innerTab.table.rows.map((row, rIndex) => (
                                                                                    <tr key={rIndex}>
                                                                                        {row.map((cell, cIndex) => <td key={cIndex}>{cell}</td>)}
                                                                                    </tr>
                                                                                ))}
                                                                            </tbody>
                                                                        </table>
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* Desktop Tab Content Display */}
                        <div className="tab-content mt-5 d-none d-md-block" id="mainTabsContent">
                            {mainTabs.map((mainTab, mainIndex) => (
                                <div 
                                    className={`tab-pane fade ${activeMainTab === mainTab.title ? 'show active' : ''}`} 
                                    id={`desktop-main-${mainIndex}`} 
                                    role="tabpanel" 
                                    key={`desktop-main-pane-${mainIndex}`}
                                >
                                    <div className="container">
                                        <div className="naac-container">
                                            <div className="naac-tabs-vertical">
                                                {/* Inner Tabs Navigation (Vertical) */}
                                                <div className="nav flex-column nav-tabs nss-tabs" id={`naac-vertical-tab-${mainIndex}`} role="tablist">
                                                    {mainTab.inner_tabs && mainTab.inner_tabs.map((innerTab, innerIndex) => (
                                                        <button
                                                            key={`desktop-inner-btn-${mainIndex}-${innerIndex}`}
                                                            className={`nav-link ${activeNssTab === innerTab.title ? 'active' : ''}`}
                                                            onClick={() => handleNssTabChange(innerTab.title)}
                                                            data-bs-toggle="tab"
                                                            data-bs-target={`#desktop-inner-content-${mainIndex}-${innerIndex}`}
                                                            type="button"
                                                            role="tab"
                                                        >
                                                            {innerTab.title}
                                                        </button>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Inner Tabs Content Display */}
                                            <div className="tab-content naac-content" id={`naac-vertical-tabContent-${mainIndex}`}>
                                                {mainTab.inner_tabs && mainTab.inner_tabs.map((innerTab, innerIndex) => (
                                                    <div 
                                                        key={`desktop-inner-pane-${mainIndex}-${innerIndex}`}
                                                        className={`tab-pane fade ${activeNssTab === innerTab.title ? 'show active' : ''}`} 
                                                        id={`desktop-inner-content-${mainIndex}-${innerIndex}`} 
                                                        role="tabpanel"
                                                    >
                                                        <h5 className="mb-3 d-flex justify-content-center align-items-center">{innerTab.title}</h5>
                                                        
                                                        {innerTab.content_type === 'table' && innerTab.content && (
                                                            <div dangerouslySetInnerHTML={renderContent(innerTab.content)} />
                                                        )}
                                                        
                                                        {innerTab.content_type === 'list' && (
                                                            <ActivitiesList items={innerTab.items} />
                                                        )}

                                                        {/* Handle optional image display for UBA, for example */}
                                                        {mainTab.title.includes('UBA') && innerTab.content_type === 'list' && (
                                                            <div className="row justify-content-center mt-3">
                                                                <div className="col-md-8">
                                                                    <img src="https://care.edu.in/wp-content/uploads/2021/03/uba.jpg" alt="UBA Image" className="img-fluid" />
                                                                </div>
                                                            </div>
                                                        )}

                                                        {innerTab.content_type === 'data_table' && innerTab.table && (
                                                            <div className="table-responsive">
                                                        <table className="table table-bordered">
                                                            <thead>
                                                                <tr>
                                                                    {innerTab.table.headers.map((h, i) => <th key={i}>{h}</th>)}
                                                                </tr>
                                                            </thead>
                                                            <tbody>
                                                                {innerTab.table.rows.map((row, rIndex) => (
                                                                    <tr key={rIndex}>
                                                                        {row.map((cell, cIndex) => <td key={cIndex}>{cell}</td>)}
                                                                    </tr>
                                                                ))}
                                                            </tbody>
                                                        </table>
                                                        </div>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </>
    );
};

export default NSSContent;